import { Text } from "@blockdaemon-internal/precision-ui";
import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  Flex,
  Grid,
  Group,
  Stack,
  Switch,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect, useState } from "react";
import packageJson from "../../../package.json";
import devToolsIconPurple from "../../assets/blockdeamon-badge-purple.svg";
import devToolsIconWhite from "../../assets/blockdeamon-badge-white.svg";
import { useBalance } from "../../features/portfolio/hooks/use-balance";
import { WalletConnectButton } from "../../features/wallet/components/wallet-connect-button";
import { useWalletConnection } from "../../features/wallet/hooks/use-wallet-connection";
import { WalletAdapterStatus } from "../../features/wallet/utils/types";
import { useDevTools } from "../hooks/use-dev-tools";
import { Address } from "./address";
import { ErrorTestingPlayground } from "./error-simulation-components";
import { KeyboardShortcut } from "./keyboard-shortcut";

export const DevTools = () => {
  const {
    opened,
    close,
    toggle,
    handleToggleEmbedMode,
    handleClearAppData,
    isEmbedMode,
    overrides,
  } = useDevTools();
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const { status, selectedAccount } = useWalletConnection();
  const { data: currentBalance, isLoading: isBalanceLoading } = useBalance();

  // Drawer width state and dragging logic
  const [drawerWidth, setDrawerWidth] = useState(500);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, width: 0 });

  const MIN_WIDTH = 400;
  const MAX_WIDTH = 800;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      width: drawerWidth,
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = dragStart.x - e.clientX; // Subtract because we're dragging from the left
    const newWidth = Math.min(Math.max(dragStart.width + deltaX, MIN_WIDTH), MAX_WIDTH);
    setDrawerWidth(newWidth);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle mouse events on document to support dragging outside the drawer
  // biome-ignore lint/correctness/useExhaustiveDependencies: we don't want to re-attach the event listeners on every render
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging]);

  // Get balance override state from the generic override system
  const balanceOverride = overrides.getOverride("balance");
  const isBalanceOverrideEnabled = balanceOverride?.enabled ?? false;
  const balanceOverrideValue = balanceOverride?.value ?? "";

  // Handle balance override toggle
  const handleBalanceOverrideToggle = (enabled: boolean) => {
    // Use existing value or provide a default
    const valueToUse = balanceOverrideValue || "1.0000";
    overrides.setOverride("balance", enabled, valueToUse);
  };

  // Handle balance override value change
  const handleBalanceOverrideValueChange = (value: string) => {
    // Update the value while preserving the enabled state
    overrides.setOverride("balance", isBalanceOverrideEnabled, value);
  };

  // Choose icon and colors based on color scheme
  const devToolsIcon = colorScheme === "dark" ? devToolsIconWhite : devToolsIconPurple;
  const isLightMode = colorScheme === "light";

  return (
    <>
      <Tooltip
        label={
          <Flex align="center" gap="0.5rem">
            {opened ? "Close Developer Tools" : "Open Developer Tools"}
            <KeyboardShortcut shortcut="⌘I" />
          </Flex>
        }
        position="left"
      >
        <UnstyledButton
          onClick={toggle}
          pos="fixed"
          bottom={16}
          right={16}
          bg={isLightMode ? "gray.0" : "gray.6"}
          p="0.25rem 0.5rem"
          aria-label={opened ? "Close Developer Tools" : "Open Developer Tools"}
          sx={({ colors }) => ({
            zIndex: 1001,
            borderColor: isLightMode ? colors.gray[2] : colors.gray[5],
            borderWidth: "1px",
            borderRadius: "0.25rem",
            borderStyle: "solid",
            overflow: "hidden",
          })}
        >
          <Flex gap="0.25rem" align="center">
            <Box component="img" src={devToolsIcon} alt="Blockdaemon Badge" w={14} h={14} />
            <Text c="gray.9" ff="Space Grotesk" fz="xs" fw="bold" pr={60}>
              DevTools
            </Text>
            <Badge
              sx={(theme) => ({
                textTransform: "none",
                backgroundColor: theme.fn.primaryColor(),
                color: theme.white,
              })}
              w={60}
              pos="absolute"
              right={0}
              top={0}
              h={24}
            >
              v{packageJson.version}
            </Badge>
          </Flex>
        </UnstyledButton>
      </Tooltip>
      <Drawer
        opened={opened}
        onClose={close}
        title={
          <Group spacing="sm">
            <Box component="img" src={devToolsIcon} alt="Blockdaemon Badge" w={20} h={20} />
            <Title order={3}>Developer Tools</Title>
            <Badge
              size="sm"
              sx={(theme) => ({
                textTransform: "none",
                backgroundColor: theme.fn.primaryColor(),
                color: theme.white,
              })}
            >
              v{packageJson.version}
            </Badge>
          </Group>
        }
        overlayProps={{ bg: "transparent" }}
        position="right"
        size={drawerWidth}
        closeOnEscape
        closeOnClickOutside
        styles={{
          content: {
            borderWidth: "0 !important",
            boxShadow: "0px 1px 3px 0px rgba(33,27,78,0.15)",
          },
          body: {
            paddingLeft: "16px", // Account for resize handle space
            paddingBottom: "16px", // Reduce bottom padding
          },
        }}
      >
        {/* Left resize handle */}
        <Box
          onMouseDown={handleMouseDown}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 8,
            height: "100%",
            cursor: "ew-resize",
            backgroundColor: isDragging ? theme.fn.rgba(theme.colors.blue[6], 0.2) : "transparent",
            borderLeft: isDragging ? `2px solid ${theme.colors.blue[6]}` : "2px solid transparent",
            transition: isDragging ? "none" : "all 0.2s ease",
            zIndex: 10,
          }}
        >
          {/* Visual indicator for the resize handle */}
          <Box
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 3,
              height: 24,
              backgroundColor: isDragging ? theme.colors.blue[6] : theme.colors.gray[3],
              borderRadius: 2,
              transition: "background-color 0.2s ease",
            }}
          />
        </Box>
        <Stack spacing="lg" style={{ minHeight: "fit-content" }}>
          {/* App Configuration Section */}
          <Card>
            <Stack spacing="md">
              <Group spacing="sm">
                <Text fw={600} size="sm">
                  ⚙️ App Configuration
                </Text>
              </Group>

              <Switch
                label="Embed Mode"
                description={`Switch between embed and standalone layouts. Currently in ${isEmbedMode ? "embed" : "standalone"} mode.`}
                checked={isEmbedMode}
                onChange={handleToggleEmbedMode}
                size="md"
                styles={{
                  label: {
                    fontWeight: 500,
                  },
                  description: {
                    fontSize: "0.8rem",
                    marginTop: "0.25rem",
                  },
                }}
              />
            </Stack>
          </Card>

          {/* Wallet Connection Section */}
          <Card>
            <Stack spacing="md">
              <Group spacing="sm">
                <Text fw={600} size="sm">
                  🔗 Wallet Connection
                </Text>
              </Group>

              <Grid>
                <Grid.Col span={6}>
                  <Stack spacing="xs">
                    <Text size="xs" c="gray.9" tt="uppercase" fw={500}>
                      Wallet Type
                    </Text>
                    <Badge variant="blue">AppKit</Badge>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Stack spacing="xs">
                    <Text size="xs" c="gray.9" tt="uppercase" fw={500}>
                      Connection Status
                    </Text>
                    <Badge
                      variant={
                        status === WalletAdapterStatus.CONNECTED
                          ? "green"
                          : status === WalletAdapterStatus.CONNECTING
                            ? "yellow"
                            : status === WalletAdapterStatus.ERROR
                              ? "red"
                              : "gray"
                      }
                    >
                      {status.toLowerCase()}
                    </Badge>
                  </Stack>
                </Grid.Col>
              </Grid>

              {selectedAccount && (
                <Box>
                  <Text size="xs" c="gray.9" tt="uppercase" fw={500} mb="xs">
                    Connected Address
                  </Text>
                  <Address address={selectedAccount.address} sliceStart={8} sliceEnd={6} />
                </Box>
              )}
            </Stack>
          </Card>

          {/* Wallet Info Section */}
          <Card>
            <Stack spacing="md">
              <Group spacing="sm">
                <Text fw={600} size="sm">
                  💰 Wallet Info
                </Text>
              </Group>

              {selectedAccount ? (
                <Stack spacing="sm">
                  <Box>
                    <Text size="xs" c="gray.9" tt="uppercase" fw={500} mb="xs">
                      Current Balance
                    </Text>
                    <Text size="sm">
                      {isBalanceLoading ? "Loading..." : currentBalance || "No balance data"}
                    </Text>
                  </Box>

                  <Divider />

                  <Stack spacing="xs">
                    <Text size="xs" c="gray.9" tt="uppercase" fw={500}>
                      Override Balance
                    </Text>
                    <Switch
                      label="Enable balance override"
                      size="sm"
                      checked={isBalanceOverrideEnabled}
                      onChange={(event) => handleBalanceOverrideToggle(event.currentTarget.checked)}
                      styles={{
                        label: {
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                    <Box>
                      <TextInput
                        placeholder="Enter balance (ETH)"
                        value={balanceOverrideValue}
                        onChange={(event) =>
                          handleBalanceOverrideValueChange(event.currentTarget.value)
                        }
                        disabled={!isBalanceOverrideEnabled}
                      />
                    </Box>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing="sm">
                  <Text size="sm" c="gray.9">
                    Connect a wallet to override balance
                  </Text>
                  <WalletConnectButton />
                </Stack>
              )}
            </Stack>
          </Card>

          {/* Error Testing Components */}
          <ErrorTestingPlayground />

          {/* Developer Resources Section */}
          <Card>
            <Stack spacing="md">
              <Group spacing="sm">
                <Text fw={600} size="sm" c="gray.9">
                  📚 Developer Resources
                </Text>
              </Group>

              <Text size="sm" c="gray.9">
                Access comprehensive documentation, integration guides, API references, and SDK
                documentation.
              </Text>

              <Button
                component="a"
                href={process.env.NODE_ENV === "development" ? "http://localhost:6006" : "/docs/"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Storybook Documentation
              </Button>

              <Button
                component="a"
                href="https://bd-sdk-e0817e.gitlab.io/"
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary"
              >
                Open SDK Documentation
              </Button>
            </Stack>
          </Card>

          {/* Actions Section */}
          <Card>
            <Stack spacing="md">
              <Group spacing="sm">
                <Text fw={600} size="sm" c="gray.9">
                  🗑️ Data Management
                </Text>
              </Group>

              <Text size="sm" c="gray.9">
                Clear all stored application data including wallet connections, preferences, and
                cache.
              </Text>

              <Button onClick={handleClearAppData} variant="destructive">
                Clear All App Data
              </Button>
            </Stack>
          </Card>
        </Stack>

        {/* Dev tools rendered outside of drawer to prevent layout interference */}
        {process.env.NODE_ENV === "development" && (
          <>
            <ReactQueryDevtools position="bottom" />
            <TanStackRouterDevtools position="bottom-left" />
          </>
        )}
      </Drawer>
    </>
  );
};
